import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, Button, Card, Upload, message, 
  Typography, Space, Modal, Select, Progress, Tag 
} from 'antd';
import { 
  UploadOutlined, EditOutlined, DeleteOutlined, 
  FileTextOutlined, CheckCircleOutlined, SyncOutlined,
  ExclamationCircleOutlined, ClockCircleOutlined,
  CheckOutlined, CloseOutlined
} from '@ant-design/icons';
import { pdfApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PDFPreview from '../../components/PDFContentEditor/PDFPreview';

const { Title } = Typography;
const { Option } = Select;

const FileList = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [extracting, setExtracting] = useState({});
  const [progress, setProgress] = useState({});
  const [messageApi, contextHolder] = message.useMessage();

  // 获取文件列表
  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      // 检查是否有token
      const token = localStorage.getItem('token');
      if (!token) {
        messageApi.error('请先登录');
        navigate('/login');
        return;
      }

      const response = await pdfApi.getFiles();
      console.log('文件列表响应:', response); // 添加调试日志
      
      // 确保数据格式正确
      const fileList = response.items || [];
      
      // 确保每个文件对象都包含必要的字段
      const processedFiles = fileList.map(file => ({
        ...file,
        status: file.status || '待解析',
        uploader: file.uploader || user,
        match_status: file.match_status || '未匹配',
        subject: file.subject || '未分类',
        content_type: file.content_type || '题目',
        page_count: file.page_count || 0
      }));
      
      setFiles(processedFiles);
    } catch (err) {
      console.error('获取文件列表失败:', err);
      if (err.response?.status === 401) {
        messageApi.error('登录已过期，请重新登录');
        logout();
        navigate('/login');
      } else {
        messageApi.error('获取文件列表失败：' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, logout, user, messageApi]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.error('请先登录');
      navigate('/login');
      return;
    }
    
    if (!user) {
      messageApi.error('请先登录');
      navigate('/login');
      return;
    }
    
    fetchFiles();
  }, [user, navigate, fetchFiles, messageApi]);

  // 处理文件上传
  const handleUpload = async (info) => {
    const { status, name, originFileObj } = info.file;
    
    if (status === 'uploading') {
      return;
    }

    if (!selectedSubject) {
      messageApi.warning('请先选择科目');
      return;
    }

    if (!selectedContentType) {
      messageApi.warning('请先选择内容类型');
      return;
    }

    try {
      if (status === 'done' || status === undefined) {
        const response = await pdfApi.uploadFile(originFileObj, selectedSubject, selectedContentType);
        console.log('上传响应:', response); // 添加调试日志
        
        if (response.data && response.data.id) {
          messageApi.success(`${name} 上传成功`);
          
          // 获取完整的文件信息
          const fileResponse = await pdfApi.getFile(response.data.id);
          if (fileResponse.data) {
            const uploadedFile = {
              ...fileResponse.data,
              status: '待解析',
              uploader: user,
              match_status: '未匹配',
              subject: selectedSubject,
              content_type: selectedContentType,
              page_count: fileResponse.data.page_count || 0
            };
            
            // 预览新上传的文件
            handlePreview(uploadedFile);
          }
          
          // 刷新文件列表
          fetchFiles();
        } else {
          messageApi.error(`${name} 上传失败：服务器响应格式错误`);
        }
      }
    } catch (err) {
      console.error('文件上传失败:', err);
      const errorStatus = err.response?.status;
      const errorMsg = err.response?.data?.message || err.message || '未知错误';
      
      if (errorStatus === 401) {
        messageApi.error('登录已过期，请重新登录');
        logout();
        navigate('/login');
      } else {
        messageApi.error(`${name} 上传失败: ${errorMsg}`);
      }
    }
  };

  // 处理文件预览
  const handlePreview = async (file) => {
    try {
      // 获取文件的签名URL
      const response = await pdfApi.getSignedUrl(file.id);
      console.log("获取签名URL响应:", response);
      
      const fileUrl = response.data?.signed_url;
      if (!fileUrl) {
        throw new Error('获取签名URL失败：响应数据格式错误');
      }
      
      setCurrentFile({
        ...file,
        previewUrl: fileUrl
      });
      setPreviewVisible(true);
    } catch (err) {
      console.error('获取签名URL失败:', err);
      messageApi.error('获取文件URL失败：' + (err.response?.data?.detail || err.message));
    }
  };

  // 开始提取内容
  const handleExtract = async (file) => {
    try {
      setExtracting({ ...extracting, [file.id]: true });
      const response = await pdfApi.startExtract(file.id, file.subject);
      console.log('开始提取响应:', response);
      
      messageApi.success('开始提取内容');
      
      // 开始轮询进度
      const interval = setInterval(async () => {
        try {
          const statusResponse = await pdfApi.getExtractStatus(file.id);
          console.log('提取状态响应:', statusResponse);
          
          if (!statusResponse.data) {
            throw new Error('获取提取状态失败：响应数据为空');
          }
          
          const progressData = {
            status: statusResponse.data.status,
            progress: statusResponse.data.progress || 0,
            message: statusResponse.data.message
          };
          
          setProgress(prev => ({
            ...prev,
            [file.id]: progressData
          }));
          
          if (progressData.status === '解析成功') {
            clearInterval(interval);
            setExtracting({ ...extracting, [file.id]: false });
            messageApi.success('内容提取完成');
            navigate(`/pdf/edit/${file.id}`);
          } else if (progressData.status === '解析失败') {
            clearInterval(interval);
            setExtracting({ ...extracting, [file.id]: false });
            messageApi.error('内容解析失败：' + progressData.message);
          }
        } catch (err) {
          console.error('轮询进度出错:', err);
          clearInterval(interval);
          setExtracting({ ...extracting, [file.id]: false });
          messageApi.error('获取提取进度失败：' + (err.response?.data?.message || err.message));
        }
      }, 2000); // 增加轮询间隔到2秒
    } catch (err) {
      console.error('开始提取失败:', err);
      setExtracting({ ...extracting, [file.id]: false });
      messageApi.error('开始提取失败: ' + (err.response?.data?.message || err.message));
    }
  };

  // 处理删除文件
  const handleDelete = async (id) => {
    try {
      await pdfApi.deleteFile(id);
      messageApi.success('文件删除成功');
      fetchFiles();
    } catch (err) {
      messageApi.error('删除失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
    },
    {
      title: '上传时间',
      dataIndex: 'upload_time',
      key: 'upload_time',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '上传人',
      dataIndex: 'uploader',
      key: 'uploader',
      render: (uploader) => {
        if (!uploader) return '未知';
        return uploader.fullname || uploader.username || uploader.name || '未知';
      }
    },
    {
      title: '页数',
      dataIndex: 'page_count',
      key: 'page_count',
    },
    {
      title: '科目',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: '内容类型',
      dataIndex: 'content_type',
      key: 'content_type',
    },
    {
      title: '匹配状态',
      dataIndex: 'match_status',
      key: 'match_status',
      render: (text) => {
        let icon = null;
        let color = '';
        
        switch (text) {
          case '已匹配':
            icon = <CheckOutlined />;
            color = '#52c41a';
            break;
          case '未匹配':
          default:
            icon = <CloseOutlined />;
            color = '#bfbfbf';
            break;
        }
        
        return (
          <Tag 
            icon={icon}
            style={{ 
              backgroundColor: text === '已匹配' ? '#f6ffed' : '#f5f5f5',
              color: color,
              borderColor: color,
              fontWeight: text === '已匹配' ? 'bold' : 'normal'
            }}
          >
            {text || '未匹配'}
          </Tag>
        );
      },
    },
    {
      title: '解析状态',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        let icon = null;
        let color = '';
        
        switch (text) {
          case '解析成功':
            icon = <CheckCircleOutlined style={{ color: '#2e7d32' }} />;
            color = '#2e7d32';
            break;
          case '提取中':
            icon = <SyncOutlined spin style={{ color: '#004d40' }} />;
            color = '#004d40';
            break;
          case '解析失败':
            icon = <ExclamationCircleOutlined style={{ color: '#d32f2f' }} />;
            color = '#d32f2f';
            break;
          case '待解析':
          default:
            icon = <ClockCircleOutlined style={{ color: '#00695c' }} />;
            color = '#00695c';
            break;
        }
        
        return (
          <Space>
            {icon}
            <span style={{ color }}>{text}</span>
            {extracting[record.id] && progress[record.id] && (
              <Progress 
                percent={progress[record.id].progress} 
                size="small" 
                style={{ width: 100 }}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {record.status === '待解析' && (
            <Button 
              type="primary"
              icon={<FileTextOutlined />}
              onClick={async () => {
                try {
                  // 获取文件的签名URL
                  const urlData = await pdfApi.getSignedUrl(record.id);
                  const fileUrl = urlData.data.signed_url;
                  console.log("获取到签名URL:", fileUrl);
                  
                  setCurrentFile({
                    ...record,
                    previewUrl: fileUrl
                  });
                  setPreviewVisible(true);
                } catch (err) {
                  console.error('获取签名URL失败:', err);
                  messageApi.error('获取文件URL失败：' + (err.response?.data?.detail || err.message));
                }
              }}
            >
              解析
            </Button>
          )}
          {record.status === '解析成功' && (
            <Button 
              icon={<EditOutlined />}
              onClick={() => navigate(`/pdf/edit/${record.id}`)}
            >
              查看
            </Button>
          )}
          <Button 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="file-list-container">
      {contextHolder}
      <div className="file-list">
        <Title level={2}>文档解析</Title>
        
        <Card style={{ marginBottom: 16, backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
          <div style={{ color: '#004d40' }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>功能说明：</div>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>支持上传扫描版PDF格式的试卷、讲义等教学文档</li>
              <li>自动识别文档内容，提取文本、公式、图表等</li>
              <li>提取后可在线编辑、整理文档内容</li>
              <li>识别结果可在其他工具内进行调用</li>
            </ul>
          </div>
        </Card>
        
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <Select
              style={{ width: 200 }}
              placeholder="请选择科目"
              onChange={value => setSelectedSubject(value)}
              value={selectedSubject}
            >
              <Option value="大学语文">大学语文</Option>
              <Option value="高等数学">高等数学</Option>
              <Option value="计算机等级考试">计算机等级考试</Option>
              <Option value="英语四级等级考试">英语四级等级考试</Option>
              <Option value="日语四级等级考试">日语四级等级考试</Option>
              <Option value="财经类">财经类</Option>
              <Option value="管理类">管理类</Option>
              <Option value="电子信息类">电子信息类</Option>
              <Option value="计算机类">计算机类</Option>
              <Option value="机械工程类">机械工程类</Option>
              <Option value="音乐类">音乐类</Option>
              <Option value="美术设计类">美术设计类</Option>
              <Option value="化工生物类">化工生物类</Option>
              <Option value="文史类">文史类</Option>
              <Option value="土木建筑类">土木建筑类</Option>
              <Option value="新闻传播类">新闻传播类</Option>
              <Option value="医护类">医护类</Option>
              <Option value="日语类">日语类</Option>
              <Option value="英语类">英语类</Option>
              <Option value="法学类">法学类</Option>
              <Option value="教育类">教育类</Option>
              <Option value="资源环境类">资源环境类</Option>
              <Option value="农林类">农林类</Option>
              <Option value="食品类">食品类</Option>
              <Option value="其他">其他</Option>
            </Select>
            <Select
              style={{ width: 200 }}
              placeholder="请选择内容类型"
              onChange={value => setSelectedContentType(value)}
              value={selectedContentType}
            >
              <Option value="题目">题目</Option>
              <Option value="答案">答案</Option>
              <Option value="题目和答案">题目和答案</Option>
              <Option value="其他">其他</Option>
            </Select>
            <Upload
              accept=".pdf"
              maxSize={500 * 1024 * 1024}
              customRequest={({ file, onSuccess, onError }) => {
                handleUpload({ file: { ...file, status: undefined, name: file.name, originFileObj: file } })
                  .then(() => onSuccess())
                  .catch(err => onError(err));
              }}
              showUploadList={false}
              onPreview={handlePreview}
            >
              <Button icon={<UploadOutlined />}>上传PDF文件</Button>
            </Upload>
          </Space>
        </Card>
        
        <Card>
          <Table 
            columns={columns} 
            dataSource={files} 
            rowKey="id" 
            loading={loading}
            pagination={{ defaultPageSize: 10 }}
          />
        </Card>
      </div>
      {currentFile && (
        <div className="pdf-preview-container">
          <Modal
            title="PDF预览"
            open={previewVisible}
            onCancel={() => {
              setPreviewVisible(false);
              setCurrentFile(null);
            }}
            width="90%"
            style={{ 
              top: '5%',
              paddingBottom: '5%',
              maxWidth: '1000px',
            }}
            styles={{
              body: {
                height: '75vh',
                overflow: 'auto'
              }
            }}
            footer={[
              <Button 
                key="extract"
                type="primary" 
                onClick={() => handleExtract(currentFile)}
                disabled={extracting[currentFile?.id]}
              >
                开始解析内容
              </Button>
            ]}
          >
            <PDFPreview file={currentFile.previewUrl} mode="modal" />
          </Modal>
        </div>
      )}
    </div>
  );
};

export default FileList; 